import React from "react";

import { Box, IconButton, Modal, Typography } from "@mui/material";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";

import { ReactComponent as CloseIcon } from "assets/images/icons/btn_close.svg";

const normalizeTimestamp = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numberValue = Number(value);

  if (!Number.isNaN(numberValue)) {
    return numberValue < 100000000000 ? numberValue * 1000 : numberValue;
  }

  const parsedValue = Date.parse(value);

  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const formatExpireAt = (value) => {
  const timestamp = normalizeTimestamp(value);

  if (!timestamp) {
    return "-";
  }

  const date = new Date(timestamp);

  const dateText = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const remainingDays = Math.max(
    0,
    Math.ceil((timestamp - Date.now()) / (24 * 60 * 60 * 1000)),
  );

  return `${dateText} (in ${remainingDays} days)`;
};

const formatAccessLevel = (value) => {
  switch (value) {
    case "read_only":
    case "read-only":
      return "Read-only";

    case "full_control":
    case "full-control":
      return "Full-control";

    default:
      return value || "-";
  }
};

const DetailRow = ({ label, value, valueWidth = "1fr", bold = false }) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `170px ${valueWidth}`,
        columnGap: "12px",
        alignItems: "flex-start",
      }}
    >
      <Typography
        sx={{
          color: "#737B90",
          fontSize: "14px",
          lineHeight: 1.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          minWidth: 0,
          color: "#172B68",
          fontSize: "14px",
          fontWeight: bold ? 700 : 500,
          lineHeight: 1.5,
          overflowWrap: "anywhere",
        }}
      >
        {value || "-"}
      </Typography>
    </Box>
  );
};

const PreviewModal = ({
  open,
  loading = false,
  previewData,
  onClose,
  onRedeem,
}) => {
  const data = previewData || {};

  const delegatedTo =
    data?.delegatedToDisplayName ??
    data?.supportDisplayName ??
    data?.currentSupportDisplayName ??
    data?.supportUserDisplayName ??
    "-";

  const scope =
    data?.scopeLabel ?? data?.scopeName ?? data?.customerScope ?? "-";

  const sharedFrom =
    data?.sharedFrom ?? data?.sharedFromDisplayName ?? data?.generatedBy ?? "-";

  const region = data?.regionName ?? data?.region ?? data?.regionSummary ?? "-";

  const accessLevel = formatAccessLevel(data?.accessLevel);

  const expires = formatExpireAt(data?.expireAt ?? data?.expiresAt);

  const sites = data?.sites ?? data?.siteCount ?? data?.delegatedSiteCount ?? 0;

  const accessPoints =
    data?.accessPoints ?? data?.accessPointCount ?? data?.apCount ?? 0;

  const switches = data?.switches ?? data?.switchCount ?? 0;

  const routers = data?.routers ?? data?.routerCount ?? 0;

  const totalDevices =
    data?.totalDevices ??
    data?.deviceCount ??
    Number(accessPoints) + Number(switches) + Number(routers);

  return (
    <Modal
      open={open}
      onClose={loading ? undefined : onClose}
      aria-labelledby="redeem-preview-title"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(31, 42, 68, 0.45)",
          },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",

          width: "540px",
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - 32px)",

          display: "flex",
          flexDirection: "column",

          bgcolor: "#FFFFFF",
          borderRadius: "12px",
          boxShadow: "0 18px 50px rgba(25, 35, 70, 0.28)",
          overflow: "hidden",
          outline: "none",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            minHeight: "66px",
            px: "14px",
            py: "12px",
            boxSizing: "border-box",

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            borderBottom: "1px solid #DDE2EB",
          }}
        >
          <Typography
            id="redeem-preview-title"
            sx={{
              color: "#172B68",
              fontSize: "18px",
              fontWeight: 700,
            }}
          >
            Review what's being delegated
          </Typography>

          <IconButton
            onClick={onClose}
            disabled={loading}
            aria-label="Close preview modal"
            sx={{
              width: "36px",
              height: "36px",
              color: "#8A94A8",

              "&:hover": {
                bgcolor: "#F3F5F9",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: "13px",
            py: "22px",
          }}
        >
          <Typography
            sx={{
              mb: "14px",
              color: "#737B90",
              fontSize: "14px",
              lineHeight: 1.25,
            }}
          >
            Redeeming binds this token to{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {delegatedTo}
            </Box>{" "}
            and adds it to{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              Delegated to me
            </Box>
            .
            <br />
            The customer sees your actions under this Employee ID — never your
            name.
          </Typography>

          <Box
            sx={{
              p: "22px 20px",
              border: "1px solid #DDE3FA",
              borderRadius: "8px",
              bgcolor: "#F1F3FF",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1px",
              }}
            >
              <DetailRow label="Scope" value={scope} bold />

              <DetailRow label="Shared from" value={sharedFrom} />

              <DetailRow label="Region" value={region} />

              <DetailRow label="Access level" value={accessLevel} bold />

              <DetailRow label="Expires" value={expires} bold />
            </Box>

            <Typography
              sx={{
                mt: "10px",
                mb: "2px",
                color: "#172B68",
                fontSize: "14px",
                fontWeight: 700,
              }}
            >
              You will be delegated
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <DetailRow label="Sites" value={String(sites)} />

              <DetailRow
                label="Devices"
                value={
                  <>
                    {accessPoints} Access Points · {switches} Switches ·{" "}
                    {routers} Routers{" "}
                    <Box
                      component="span"
                      sx={{
                        color: "#737B90",
                        fontWeight: 600,
                      }}
                    >
                      ({totalDevices} total)
                    </Box>
                  </>
                }
              />
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            minHeight: "72px",
            px: "13px",
            py: "16px",
            boxSizing: "border-box",

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            borderTop: "1px solid #DDE2EB",
            bgcolor: "#FAFBFD",
          }}
        >
          <OutlineButton
            width="80px"
            height="32px"
            disabled={loading}
            onClick={onClose}
          >
            Cancel
          </OutlineButton>
          <SolidButton
            disabled={loading}
            onClick={onRedeem}
            width="120px"
            height="32px"
          >
            {loading ? "Redeeming..." : "Redeem token"}
          </SolidButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default PreviewModal;
